/**
 * Time Entry Types
 *
 * Types for workshop time tracking on work orders.
 *
 * Data model:
 * - TimeEntry: Completed time sessions stored under /teams/{teamId}/workOrders/{workOrderId}/timeEntries
 * - ActiveTimer: Running timer state stored under /teams/{teamId}/activeTimers/{mechanicId}
 *
 * Critical design decision:
 * - timeEntries represent COMPLETED sessions only (no isActive field)
 * - Running state lives ONLY in activeTimers collection
 * - One active timer per mechanic (doc id = mechanicId/auth uid)
 */

import type { Timestamp } from 'firebase/firestore';

/**
 * Source of a time entry
 * - TIMER: Created by stopping an active timer
 * - MANUAL: Manually entered by user
 */
export type TimeEntrySource = 'TIMER' | 'MANUAL';

/**
 * Time Entry
 *
 * Represents a completed time session on a work order.
 * Created when a timer is stopped or manually entered.
 *
 * Firestore path: teams/{teamId}/workOrders/{workOrderId}/timeEntries/{timeEntryId}
 */
export interface TimeEntry {
  id: string;
  workOrderId: string;
  mechanicId: string;
  mechanicName?: string; // Denormalized for display

  // Time tracking
  startedAt: string; // ISO timestamp
  endedAt: string; // ISO timestamp
  durationMinutes: number; // Computed: (endedAt - startedAt) in minutes

  // Rate snapshots (captured at time of entry for historical accuracy)
  hourlyCostSnapshot: number; // Internal cost rate (€/hour)
  hourlySellSnapshot?: number; // Optional sell rate (€/hour) - for future billing

  // Computed totals
  totalCost: number; // durationMinutes/60 * hourlyCostSnapshot
  totalSell?: number; // durationMinutes/60 * hourlySellSnapshot

  // Metadata
  notes?: string;
  source: TimeEntrySource;

  // Audit
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  createdBy: string;
}

/**
 * Time Entry Firestore Document
 * Uses Firestore Timestamps instead of ISO strings
 */
export interface TimeEntryDocument extends Omit<
  TimeEntry,
  'startedAt' | 'endedAt' | 'createdAt' | 'updatedAt'
> {
  startedAt: Timestamp;
  endedAt: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Input for creating a time entry from a stopped timer
 */
export interface TimeEntryFromTimerInput {
  workOrderId: string;
  mechanicId: string;
  mechanicName?: string;
  startedAt: Timestamp;
  endedAt: Timestamp;
  hourlyCostSnapshot: number;
  hourlySellSnapshot?: number;
  notes?: string;
}

/**
 * Input for creating a manual time entry
 */
export interface ManualTimeEntryInput {
  mechanicId: string;
  mechanicName?: string;
  startedAt: string; // ISO timestamp or date string
  endedAt: string; // ISO timestamp or date string
  durationMinutes?: number; // Optional override (if not provided, computed from start/end)
  hourlyCostSnapshot: number;
  hourlySellSnapshot?: number;
  notes?: string;
}

/**
 * Input for updating a time entry
 */
export interface TimeEntryUpdateInput {
  startedAt?: string;
  endedAt?: string;
  durationMinutes?: number;
  hourlyCostSnapshot?: number;
  hourlySellSnapshot?: number;
  notes?: string;
}

/**
 * Active Timer
 *
 * Represents a currently running timer for a mechanic.
 * Only ONE active timer per mechanic (doc id = mechanicId).
 *
 * Firestore path: teams/{teamId}/activeTimers/{mechanicId}
 */
export interface ActiveTimer {
  mechanicId: string;
  mechanicName?: string; // Denormalized for display

  // Work order reference
  workOrderId: string;

  // Denormalized fields for UI convenience (avoid extra reads)
  workOrderTitle?: string;
  vehicleId?: string;
  vehiclePlate?: string;

  // Timer state
  startedAt: string; // ISO timestamp

  // Rate snapshots (captured at timer start)
  hourlyCostSnapshot: number;
  hourlySellSnapshot?: number;

  // Audit
  updatedAt: string; // ISO timestamp
}

/**
 * Active Timer Firestore Document
 * Uses Firestore Timestamps instead of ISO strings
 */
export interface ActiveTimerDocument extends Omit<ActiveTimer, 'startedAt' | 'updatedAt'> {
  startedAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Input for starting a timer
 */
export interface StartTimerInput {
  workOrderId: string;
  workOrderTitle?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  hourlyCostSnapshot: number;
  hourlySellSnapshot?: number;
}

/**
 * Result of starting a timer (includes info about auto-stopped previous timer)
 */
export interface StartTimerResult {
  activeTimer: ActiveTimer;
  autoStoppedPrevious?: {
    workOrderId: string;
    timeEntryId: string;
    durationMinutes: number;
  };
  /** True if work order status was auto-updated to IN_PROGRESS */
  statusUpdated?: boolean;
}

/**
 * Result of stopping a timer
 */
export interface StopTimerResult {
  timeEntry: TimeEntry;
  durationMinutes: number;
}

/**
 * Helper to convert Firestore Timestamp to ISO string
 */
export function timestampToIso(timestamp: Timestamp | null | undefined): string {
  if (!timestamp) return '';
  return timestamp.toDate().toISOString();
}

/**
 * Helper to compute duration in minutes between two timestamps
 */
export function computeDurationMinutes(startedAt: Timestamp, endedAt: Timestamp): number {
  const startMs = startedAt.toMillis();
  const endMs = endedAt.toMillis();
  return Math.round((endMs - startMs) / 60000); // Convert ms to minutes
}

/**
 * Helper to compute total cost from duration and hourly rate
 */
export function computeTotalCost(durationMinutes: number, hourlyRate: number): number {
  return Math.round((durationMinutes / 60) * hourlyRate * 100) / 100; // Round to 2 decimals
}
